import * as React from "react";
import { useState } from "react"; // Importa useState
import {
  makeStyles,
  shorthands,
  useId,
  Input,
  Label,
  Button,
  Subtitle1,
  Divider,
  Body1,
  Link,
  tokens,
  Toast,
  ToastTitle,
  useToastController,
} from "@fluentui/react-components";
import type { InputProps } from "@fluentui/react-components";
import { getPublicKey, aes256CbcEncrypt, aes256CbcDecrypt, encryptData } from "../../utils/encryption";
import * as forge from "node-forge";
import { useStateStore } from "../../store";
import { t } from "../../utils/translations";

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground3,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    paddingTop: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingBottom: "10px",
    // Use 2px gap below the label (per the design system)
    ...shorthands.gap("2px"),
    // Prevent the example from taking the full width of the page (optional)
    maxWidth: "400px",
    height: "100%",
    "> div": {
      display: "flex",
      flexDirection: "column",
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "start",
    width: "100%",
    "> div": {
      display: "flex",
      flexDirection: "column",
      marginBottom: "10px",
      width: "100%",
    },
  },
  button: {
    width: "100%",
    marginTop: "10px",
  },
  loginTitle: {
    marginBottom: "20px",
  },
  ssoTitle: {
    marginBottom: "5px",
  },
  ssoEmail: {
    marginTop: "10px",
    marginBottom: "10px",
    alignSelf: "center",
    fontWeight: "bold",
  },
  createHere: {
    fontWeight: "bold",
  },
});

interface LoginProps extends InputProps {
  onTrySso: () => void;
}

const Login = ({ onTrySso, ...props }: LoginProps) => {
  const setFirmas = useStateStore((state) => state.setFirmas);
  const setApinetToken = useStateStore((state) => state.setApinetToken);
  const setStatus = useStateStore((state) => state.setStatus);
  const setError = useStateStore((state) => state.setError);
  const error = useStateStore((state) => state.error);
  const errorSSO = useStateStore((state) => state.errorSSO);
  const setErrorSSO = useStateStore((state) => state.setErrorSSO);
  const toasterId = useToastController();
  const status = useStateStore((state) => state.status);
  const email = Office?.context?.mailbox?.userProfile?.emailAddress;
  const styles = useStyles();
  const emailId = useId("email");
  const passwordId = useId("password");
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const login = async () => {
    const { email, password } = formData;
    const url = "https://www.know.ee/apinet/v3/users/authenticate";

    try {
      const aesEncryptKey = "kKK7vEqA/nNQFCVW4yymITp4HG1FjT4577N37L1s5qQ=";
      const key = forge.util.decode64(aesEncryptKey);
      const iv = forge.random.getBytesSync(16);
      const certPem = process.env.CERTIFICATE;
      const publicKey = getPublicKey(certPem);
      const encryptedEmail = aes256CbcEncrypt(email, key, iv);
      const encryptedPassword = aes256CbcEncrypt(password, key, iv);

      const data = {
        user: {
          email: encryptedEmail,
          password: encryptedPassword,
        },
        item: encryptData(iv, publicKey),
      };
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:
            'Token token="UH/8s1fH3i9gVriUlK2dDKWfTpk+6zj8Ve1aE1uav2cv03/DpJJfaAVho+6f\\nQIZnheT4D2c7sut3mgtOmh69f3W0C8RLtLWA0+gE+nsp+sM=\\n"',
        },
        body: JSON.stringify(data),
      };

      const response = await fetch(url, options);
      const responseData = await response.json();

      if (response.status === 200) {
        const decryptedSignatures = responseData.signatures.map((signature) => {
          return {
            ...signature,
            name: aes256CbcDecrypt(signature.name, key, iv),
            id_file: aes256CbcDecrypt(signature.id_file, key, iv),
            file: aes256CbcDecrypt(signature.file, key, iv),
            background_meeting: aes256CbcDecrypt(signature.background_meeting, key, iv),
            background_preview: aes256CbcDecrypt(signature.background_preview, key, iv),
          };
        });

        setFirmas(decryptedSignatures);
        setApinetToken(aes256CbcDecrypt(responseData.apinet_token, key, iv));
        setStatus("success");
      } else {
        setStatus("loginfailed");
        setError(responseData.message || t("loginError"));
      }
    } catch (error) {
      setStatus("loginfailed");
      setError(t("loginError"));
    }
  };

  return (
    <div className={styles.root}>
      <div>
        <Subtitle1 className={styles.loginTitle}>{t("loginTitle")}</Subtitle1>
        <div className={styles.container}>
          <div>
            <Label htmlFor={emailId}>{t("emailLabel")}</Label>
            <Input
              id={emailId}
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              {...props}
            />
          </div>
          <div>
            <Label htmlFor={passwordId}>{t("passwordLabel")}</Label>
            <Input
              id={passwordId}
              name="password"
              placeholder="Password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              {...props}
            />
          </div>
          <div>
            <Button className={styles.button} appearance="primary" onClick={login}>
              {t("loginButton")}
            </Button>
          </div>
        </div>
        <div style={cssStyles.divider}>
          <Divider appearance="subtle">{t("dividerText")}</Divider>
        </div>
        <div className={styles.container}>
          <Subtitle1 className={styles.ssoTitle}>{t("ssoSubtitle")}</Subtitle1>
          <Body1>{t("ssoBody")}</Body1>
          <Body1 className={styles.ssoEmail}>{email}</Body1>
          <Button className={styles.button} appearance="secondary" onClick={onTrySso}>
            {t("trySso")}
          </Button>
        </div>
        <div style={cssStyles.linkContainer}>
          <Body1>
            {t("noAccount")}{" "}
            <Link
              className={styles.createHere}
              href="https://www.know.ee/register/registration/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("createHere")}
            </Link>
          </Body1>
        </div>
      </div>
    </div>
  );
};

const cssStyles = {
  container: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
  },
  divider: {
    marginTop: "20px",
    marginBottom: "20px",
  },
  loginSsoContainer: {
    width: "100%",
    padding: "10px",
  },
  itemContainer: {
    width: "100%",
    padding: "10px",
  },
  linkContainer: {
    marginTop: "20px",
    textAlign: "center" as const,
  },
  link: {
    textDecoration: "none",
    fontWeight: "bold",
  },
};

export default Login;
